import React, { useState, useEffect } from 'react';
import SalesDetailPage from './SalesDetailPage';
import PaymentsActivitiesSummary from './PaymentsActivitiesSummary';
import {
  SORT_TYPES, PAYMENTS_COLUMNS, MONEY_COLUMN_IDS, getDefaultVisibleColumns, getDefaultHiddenColumns,
} from '../constants/paymentsColumnsConfig';
import { buildPaymentsFilterGroups, PAYMENTS_FILTER_IDS } from '../constants/paymentsFiltersConfig';
import { compactCurrency } from '../utils/reportingUtils';
import { ToastContainer } from 'react-toastify';
import { toastError } from '../../../shared/toastHelper';

const axios = require('axios').default;

const PAYMENTS_CONFIG = {
  pageTitle: 'Payments & Activities',
  pageSubtitle: 'Payments taken per day by method, and what they paid for. Imported orders show the payments their source platform recorded; Opendate orders show their card charge or box office tender. Refunds post as negative amounts on the day they were given. A payment\'s activity split follows its order\'s mix. Days are counted in each venue\'s time zone.',
  sidebarKey: 'finances_payments',
  defaultPreset: 'last_30_days',
  dateModeOptions: [{ key: 'created_at', label: 'Payment Date' }],
  urlFilterIds: PAYMENTS_FILTER_IDS,

  sortTypes: SORT_TYPES,
  localStorageKey: (teamId) => `reportingFinancesPaymentsColumns_${teamId}`,
  firstColumnId: 'date',
  defaultSortRules: [{ field: 'date', direction: 'asc' }],

  kpiFromDetailResponse: true,
  salesDataKey: null,
  venueSeriesKey: null,
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/finances/payments/export`,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/finances/payments/data.json`,
  detailResponseKey: 'rows',
  detailErrorMessage: 'Failed to load payments data.',
  detailTableTitle: 'Payments by Day and Method',
  emptyMessage: 'No payments in this range.',
  searchPlaceholder: 'Search payment methods...',
  paginationLabel: 'rows',
  itemLabel: (count) => `row${count !== 1 ? 's' : ''}`,

  buildKpiCards: (current, prior) => [
    {
      label: 'Net received',
      value: compactCurrency(current.amount || 0),
      current: current.amount || 0,
      prior: (prior && prior.amount) || 0,
    },
    {
      label: 'Refunded',
      value: compactCurrency(current.refunds || 0),
      current: current.refunds || 0,
      prior: (prior && prior.refunds) || 0,
    },
    {
      label: 'Imported orders without payment detail',
      value: String(current.imported_orders_without_payments || 0),
      current: current.imported_orders_without_payments || 0,
      prior: (prior && prior.imported_orders_without_payments) || 0,
    },
  ],
  kpiGridColumns: 3,

  overTimeKey: null,
  topCharts: [],
  groupByOptions: [],
  groupByDefault: null,

  renderCellValue: (col, row, { currencyFormatter: cf }) => {
    if (MONEY_COLUMN_IDS.includes(col.id)) return cf.format(row[col.id] || 0);
    switch (col.id) {
      case 'date': return row.date || '—';
      case 'payment_method': return row.payment_method || '—';
      case 'payment_count': return row.payment_count ?? 0;
      default: return '—';
    }
  },

  renderNotice: (activeFilters, { team, csrfToken, dateRange }) => (
    <PaymentsActivitiesSummary
      team={team}
      csrfToken={csrfToken}
      dateRange={dateRange}
      activeFilters={activeFilters}
      filterIds={PAYMENTS_FILTER_IDS}
    />
  ),

  filterTitle: 'Filter Payments',
  filterSubtitle: 'Apply filters to narrow payments data.',
};

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  PAYMENTS_FILTER_IDS.forEach((id) => {
    if (activeFilters[id]?.length) params[id] = activeFilters[id];
  });
  return params;
};

function ReportingFinancesPaymentsPage({ team, csrfToken, venueOwnerships, ...props }) {
  const [filterOptions, setFilterOptions] = useState({});

  useEffect(() => {
    if (!team?.id) return undefined;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    let ignore = false;
    axios.get(`/teams/${team.id}/reporting/finances/payments/filter_options.json`)
      .then((res) => { if (!ignore) setFilterOptions(res.data || {}); })
      .catch(() => {
        if (!ignore) toastError('Could not load the payments filters. Reload the page to try again.');
      });
    return () => { ignore = true; };
  }, [team?.id, csrfToken]);

  const config = {
    ...PAYMENTS_CONFIG,
    columns: PAYMENTS_COLUMNS,
    getDefaultVisibleColumns,
    getDefaultHiddenColumns,
    buildFilterGroups: () => buildPaymentsFilterGroups(filterOptions, venueOwnerships),
    buildDetailApiFilters,
  };
  return (
    <>
      <SalesDetailPage
        {...props}
        team={team}
        csrfToken={csrfToken}
        venueOwnerships={venueOwnerships}
        config={config}
      />
      <ToastContainer />
    </>
  );
}

export default ReportingFinancesPaymentsPage;
